import i18n from 'i18next'
import { enUS, fi, sv } from 'date-fns/locale'

type DateFnsLocale = typeof enUS

/**
 * Locale configurations for date formatting
 * Optimized for golf application requirements
 */

// Finnish weekday abbreviations for golf scorecards
const finnishWeekdayAbbr: Record<number, string> = {
  0: 'Su', 1: 'Ma', 2: 'Ti', 3: 'Ke', 4: 'To', 5: 'Pe', 6: 'La'
}

// Swedish weekday abbreviations
const swedishWeekdayAbbr: Record<number, string> = {
  0: 'Sön', 1: 'Mån', 2: 'Tis', 3: 'Ons', 4: 'Tor', 5: 'Fre', 6: 'Lör'
}

interface DateRangeFormatters {
  sameMonth: (start: Date, end: Date, config: LocaleConfig) => string
  sameYear: (start: Date, end: Date, config: LocaleConfig) => string
  different: (start: Date, end: Date, config: LocaleConfig) => string
}

interface FormattersConfig {
  short: (date: Date, config: LocaleConfig) => string
  medium: (date: Date, config: LocaleConfig) => string
  long: (date: Date, config: LocaleConfig) => string
  weekday: (date: Date, config: LocaleConfig) => string
  time: (date: Date, config: LocaleConfig) => string
  timeSeconds: (date: Date, config: LocaleConfig) => string
  month: (date: Date, config: LocaleConfig) => string
  monthYear: (date: Date, config: LocaleConfig) => string
  dateNumeric: (date: Date, config: LocaleConfig) => string
  datetime: (date: Date, config: LocaleConfig) => string
  datetimeSeconds: (date: Date, config: LocaleConfig) => string
  dayMonth: (date: Date, config: LocaleConfig) => string
  dayMonthYear: (date: Date, config: LocaleConfig) => string
  dateRange: DateRangeFormatters
}

interface LocaleConfig {
  code: string
  dateFnsLocale: DateFnsLocale
  relative: {
    today: string
    tomorrow: string
    yesterday: string
  }
  formatters: FormattersConfig
  parser?: (dateString: string) => Date | null
}

// Locale configurations
const localeConfigs: Record<string, LocaleConfig> = {
  'en': {
    code: 'en',
    dateFnsLocale: enUS,
    relative: {
      today: 'Today',
      tomorrow: 'Tomorrow',
      yesterday: 'Yesterday'
    },
    formatters: {
      // Short format: "Sat Apr 12"
      short: (date) => {
        const weekday = date.toLocaleDateString('en-US', { weekday: 'short' })
        const month = date.toLocaleDateString('en-US', { month: 'short' })
        const day = date.getDate()
        return `${weekday} ${month} ${day}`
      },
      // Medium format: "Saturday, April 12, 2024"
      medium: (date) => {
        return date.toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      },
      // Long format with time
      long: (date) => {
        return date.toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      },
      // Weekday only
      weekday: (date) => {
        return date.toLocaleDateString('en-US', { weekday: 'long' })
      },
      // Time format for tee times
      time: (date) => {
        return date.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        })
      },
      // Time with seconds: "3:15:07 PM"
      timeSeconds: (date) => {
        return date.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          second: '2-digit',
          hour12: true
        })
      },
      // Month name only: "April"
      month: (date) => {
        return date.toLocaleDateString('en-US', { month: 'long' })
      },
      // Month and year
      monthYear: (date) => {
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long'
        })
      },
      // Numeric date only: "11/12/2025"
      dateNumeric: (date) => {
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        })
      },
      // Compact datetime format
      datetime: (date) => {
        const dateStr = date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        })
        const timeStr = date.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        })
        return `${dateStr} ${timeStr}`
      },
      // Compact datetime with seconds: "11/12/2025 3:15:07 PM"
      datetimeSeconds: (date) => {
        const dateStr = date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        })
        const timeStr = date.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          second: '2-digit',
          hour12: true
        })
        return `${dateStr} ${timeStr}`
      },
      // Day and month only: "5/16"
      dayMonth: (date) => {
        return date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })
      },
      // Day, month and year: "5/16/2026"
      dayMonthYear: (date) => {
        return date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })
      },
      // Date range formatters
      dateRange: {
        sameMonth: (start, end) => {
          const month = start.toLocaleDateString('en-US', { month: 'long' })
          const year = start.getFullYear()
          return `${month} ${start.getDate()}-${end.getDate()}, ${year}`
        },
        sameYear: (start, end) => {
          const startMonth = start.toLocaleDateString('en-US', { month: 'short' })
          const endMonth = end.toLocaleDateString('en-US', { month: 'short' })
          return `${startMonth} ${start.getDate()} - ${endMonth} ${end.getDate()}, ${start.getFullYear()}`
        },
        different: (start, end) => {
          const startStr = start.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
          })
          const endStr = end.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
          })
          return `${startStr} - ${endStr}`
        }
      }
    }
  },

  'fi': {
    code: 'fi',
    dateFnsLocale: fi,
    relative: {
      today: 'Tänään',
      tomorrow: 'Huomenna',
      yesterday: 'Eilen'
    },
    formatters: {
      // Short format: "La 12.4."
      short: (date) => {
        const weekday = finnishWeekdayAbbr[date.getDay()]
        const day = date.getDate()
        const month = date.getMonth() + 1
        return `${weekday} ${day}.${month}.`
      },
      // Medium format: "Lauantai 12. huhtikuuta 2024"
      medium: (date) => {
        const weekday = date.toLocaleDateString('fi-FI', { weekday: 'long' })
        const day = date.getDate()
        const month = date.toLocaleDateString('fi-FI', { month: 'long' })
        const year = date.getFullYear()
        return `${weekday} ${day}. ${month} ${year}`
      },
      // Long format with time
      long: (date) => {
        const weekday = date.toLocaleDateString('fi-FI', { weekday: 'long' })
        const day = date.getDate()
        const month = date.toLocaleDateString('fi-FI', { month: 'long' })
        const year = date.getFullYear()
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        return `${weekday} ${day}. ${month} ${year} klo ${hours}:${minutes}`
      },
      // Weekday only
      weekday: (date) => {
        return date.toLocaleDateString('fi-FI', { weekday: 'long' })
      },
      // Time format for tee times
      time: (date) => {
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        return `${hours}:${minutes}`
      },
      // Time with seconds: "15:15:07"
      timeSeconds: (date, config) => {
        return `${config.formatters.time(date, config)}:${String(date.getSeconds()).padStart(2, '0')}`
      },
      // Month name only: "huhtikuu"
      month: (date) => {
        return date.toLocaleDateString('fi-FI', { month: 'long' })
      },
      // Month and year
      monthYear: (date) => {
        const month = date.toLocaleDateString('fi-FI', { month: 'long' })
        const year = date.getFullYear()
        return `${month} ${year}`
      },
      // Numeric date only: "12.11.2025"
      dateNumeric: (date) => {
        const day = String(date.getDate()).padStart(2, '0')
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const year = date.getFullYear()
        return `${day}.${month}.${year}`
      },
      // Compact datetime format: "12.11.2025 15:15"
      datetime: (date) => {
        const day = String(date.getDate()).padStart(2, '0')
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const year = date.getFullYear()
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        return `${day}.${month}.${year} ${hours}:${minutes}`
      },
      // Compact datetime with seconds: "12.11.2025 15:15:07"
      datetimeSeconds: (date, config) => {
        return `${config.formatters.datetime(date, config)}:${String(date.getSeconds()).padStart(2, '0')}`
      },
      // Day and month only: "16.5."
      dayMonth: (date) => {
        return `${date.getDate()}.${date.getMonth() + 1}.`
      },
      // Day, month and year: "16.5.2026"
      dayMonthYear: (date) => {
        return `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`
      },
      // Date range formatters
      dateRange: {
        sameMonth: (start, end) => {
          const month = start.toLocaleDateString('fi-FI', { month: 'long' })
          const year = start.getFullYear()
          return `${start.getDate()}-${end.getDate()}. ${month} ${year}`
        },
        sameYear: (start, end) => {
          const startMonth = start.getMonth() + 1
          const endMonth = end.getMonth() + 1
          return `${start.getDate()}.${startMonth}. - ${end.getDate()}.${endMonth}.${start.getFullYear()}`
        },
        different: (start, end) => {
          const startStr = `${start.getDate()}.${start.getMonth() + 1}.${start.getFullYear()}`
          const endStr = `${end.getDate()}.${end.getMonth() + 1}.${end.getFullYear()}`
          return `${startStr} - ${endStr}`
        }
      }
    },
    // Finnish date parser (DD.MM.YYYY or DD.MM.)
    parser: (dateString) => {
      const parts = dateString.split('.')
      if (parts.length === 2) {
        // DD.MM. format - assume current year
        const day = parseInt(parts[0], 10)
        const month = parseInt(parts[1], 10) - 1
        const year = new Date().getFullYear()
        return new Date(year, month, day)
      } else if (parts.length === 3) {
        // DD.MM.YYYY format
        const day = parseInt(parts[0], 10)
        const month = parseInt(parts[1], 10) - 1
        const year = parseInt(parts[2], 10)
        return new Date(year, month, day)
      }
      return null
    }
  },

  'sv': {
    code: 'sv',
    dateFnsLocale: sv,
    relative: {
      today: 'Idag',
      tomorrow: 'Imorgon',
      yesterday: 'Igår'
    },
    formatters: {
      // Short format: "Lör 12 apr"
      short: (date) => {
        const weekday = swedishWeekdayAbbr[date.getDay()]
        const day = date.getDate()
        const month = date.toLocaleDateString('sv-SE', { month: 'short' })
        return `${weekday} ${day} ${month}`
      },
      // Medium format: "Lördag 12 april 2024"
      medium: (date) => {
        return date.toLocaleDateString('sv-SE', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      },
      // Long format with time
      long: (date) => {
        const dateStr = date.toLocaleDateString('sv-SE', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        return `${dateStr} kl. ${hours}:${minutes}`
      },
      // Weekday only
      weekday: (date) => {
        return date.toLocaleDateString('sv-SE', { weekday: 'long' })
      },
      // Time format for tee times
      time: (date) => {
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        return `${hours}:${minutes}`
      },
      // Time with seconds: "15:15:07"
      timeSeconds: (date, config) => {
        return `${config.formatters.time(date, config)}:${String(date.getSeconds()).padStart(2, '0')}`
      },
      // Month name only: "april"
      month: (date) => {
        return date.toLocaleDateString('sv-SE', { month: 'long' })
      },
      // Month and year
      monthYear: (date) => {
        return date.toLocaleDateString('sv-SE', {
          year: 'numeric',
          month: 'long'
        })
      },
      // Numeric date only: "2025-11-12"
      dateNumeric: (date) => {
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
      },
      // Compact datetime format: "2025-11-12 15:15"
      datetime: (date) => {
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        return `${year}-${month}-${day} ${hours}:${minutes}`
      },
      // Compact datetime with seconds: "2025-11-12 15:15:07"
      datetimeSeconds: (date, config) => {
        return `${config.formatters.datetime(date, config)}:${String(date.getSeconds()).padStart(2, '0')}`
      },
      // Day and month only: "16/5"
      dayMonth: (date) => {
        return `${date.getDate()}/${date.getMonth() + 1}`
      },
      // Day, month and year: "16/5 2026"
      dayMonthYear: (date) => {
        return `${date.getDate()}/${date.getMonth() + 1} ${date.getFullYear()}`
      },
      // Date range formatters
      dateRange: {
        sameMonth: (start, end) => {
          const month = start.toLocaleDateString('sv-SE', { month: 'long' })
          const year = start.getFullYear()
          return `${start.getDate()}-${end.getDate()} ${month} ${year}`
        },
        sameYear: (start, end) => {
          const startMonth = start.toLocaleDateString('sv-SE', { month: 'short' })
          const endMonth = end.toLocaleDateString('sv-SE', { month: 'short' })
          return `${start.getDate()} ${startMonth} - ${end.getDate()} ${endMonth} ${start.getFullYear()}`
        },
        different: (start, end) => {
          const startStr = start.toLocaleDateString('sv-SE', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
          })
          const endStr = end.toLocaleDateString('sv-SE', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
          })
          return `${startStr} - ${endStr}`
        }
      }
    }
  }
}

/**
 * Get locale configuration based on current language or specified locale
 */
export const getLocaleConfig = (locale: string | null = null): LocaleConfig => {
  // Use provided locale or detect from i18n
  const targetLocale = locale || (i18n.language?.split('-')[0]) || 'en'

  // Return config or fallback to English
  return localeConfigs[targetLocale] || localeConfigs['en']
}

